import { useQuery } from "@tanstack/react-query";
import { BarChart, Card, MIN_MODAL_CONTENT_HEIGHT } from "components";
import { useMemo, useState } from "react";
import {
  ChartExport,
  useExportableChart,
} from "../../../../../../../../shared/components";
import { Props } from "../../../../WebAnalytics.types";
import { getCustomersByGeography } from "../../Visualization.requests";

export const CustomersByGeography = ({
  pixelId,
  accountView,
  campaignId,
  startDate,
  endDate,
  accountsIds,
  visitedPages,
  includeIspTraffic,
  includeKnownBotTraffic,
  includePotentialBotTraffic,
  personas,
}: Props) => {
  const { data: rawData, isFetching: isFetching } = useQuery({
    queryKey: [
      "webAnalyticsCustomersByGeography",
      pixelId,
      accountView,
      campaignId,
      startDate,
      endDate,
      accountsIds,
      visitedPages,
      includeIspTraffic,
      includeKnownBotTraffic,
      includePotentialBotTraffic,
      personas,
    ],
    queryFn: () =>
      getCustomersByGeography(pixelId, {
        accountView,
        campaignId,
        startDate,
        endDate,
        accountsIds,
        visitedPages,
        includeIspTraffic,
        includeKnownBotTraffic,
        includePotentialBotTraffic,
        personas,
      }),
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const data = useMemo(() => {
    if (isFetching || !rawData?.data) {
      return [];
    }

    return rawData.data.map(({ value, label }) => ({
      label,
      value: Number(value),
    }));
  }, [rawData, isFetching]);

  const chart = useExportableChart({
    title: "Accounts Visited by Geography",
    campaignId: campaignId || undefined,
    chart: (
      <BarChart
        minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
        stretch
        data={data ?? []}
        orientation="horizontal"
        showXAxis={true}
      />
    ),
    dates: {
      start: startDate,
      end: endDate,
    },
  });

  return (
    <Card
      onExpandedChange={setIsExpanded}
      isLoading={isFetching}
      title={chart.title}
      hasData={!isFetching && Boolean(data?.length)}
      isExpandable
      actions={
        <ChartExport
          size="small"
          campaignId={campaignId || undefined}
          title={chart.title}
          onExportPNG={chart.downloadAsPng}
          data={data}
        />
      }
    >
      {chart.component}
      {chart.exportComponent}
    </Card>
  );
};
